define("Modules/TicketPicker", [`Module`, `module`], function (Super, Self) {
	// Global constants
	const LAYOUT_PAGINATION_COUNTS = {
		ARENA: 40,
		LIST: 40,
		ARENA_SINGLE_COLUMN: 15,
		SINGLE_COLUMN: 15,
		MULTI_COLUMN: 24,
	};
	const LIST_VIEW_LAYOUTS = [`LIST`, `ARENA`];
	const ORIENTATION = {
		SINGLE_COLUMN: `WIDE`,
		MULTI_COLUMN: `MEDIUM`,
		ARENA_SINGLE_COLUMN: `DYNAMIC`,
	};

	return Super.inherit({
		namespace: Self.id,
		requires: [`AgentPriceCode`, `ClientCart`, `Events`, `Orders`, `Products`, `ShoppingCart`, `User`],
		defaults: {
			checkUrlParams: true,
			collectionId: null, // limits the displayed sale to those matching the passed id
			groupings: null, // additional groups to sub-divide the products into
			hideAddSubtractButtonsForReserved: false, // hides the +/- buttons on the ticket picker for reserved tickets
			hideUntargetedHolds: false, // used to hide holds when targeting is in use
			maxLargeTickets: 4, // max number of sales to display before switching to 'small' modes
			preselectedProductId: null, // used to preselect a product in the list view - moves the product to the top of the list
			sales: null, // default to using setup.eventId's sale list, but allow overriding
			saleTypes: null, // primarily FIXED | ADD_ON | PRIVATE; limits the displayed sales to the passed type
			scrollOverflow: false, // whether to cap at a preset height, for Event page IMMERSIVE and FLYER view
			sortByOrdinal: true,
			transition: `fade`,
			hasPagination: false,
			hasFiltering: false,
			layoutType: `LIST`,
			moreInfoTooltipPosition: `top`,
			preferResaleToQueued: true, // whether to prefer resale button over queued button
			useClientCart: false,
		},
		constructor(setup) {
			const { App, ClientCart, Events, Orders, ShoppingCart, System, User, callbacks, data } = this;
			const {
				addOnPackCursor,
				checkUrlParams,
				collectionId,
				eventId,
				groupings,
				hideUntargetedHolds,
				layoutType,
				hasPagination,
				hasFiltering,
				hideAddSubtractButtonsForReserved,
				displayZoomButton,
				sales,
				saleTypes,
				moreInfoTooltipPosition,
				preferResaleToQueued,
				preselectedProductId,
				useClientCart,
			} = setup;
			let { scrollOverflow, sortByOrdinal } = setup;
			let initialized = false;
			let promises = [];
			let saleIds; // used to limit which sales to display based on url param
			const isListView = this.includes(LIST_VIEW_LAYOUTS, layoutType);
			const renderLayoutName = isListView ? `listView` : `singleMultiView`;
			const renderProductsFunction = isListView ? this.renderListViewProducts : this.renderSingleMultiViewProducts;

			if (scrollOverflow) {
				const system = System.get();
				const { isChrome } = system;

				this.addClass(`scroll-overflow`);

				if (isChrome) {
					this.addClass(`is-chrome`);
				}
			}

			if (checkUrlParams) {
				const queryParams = App.getQueryParams();
				this.each(queryParams, (value, key) => {
					const keyUpperCase = key.toUpperCase();
					let values;

					switch (keyUpperCase) {
						case `SID`:
							values = value.split(`,`);
							values = this.map(values, (saleId) => parseInt(saleId, 10));
							values = this.filter(values, (val) => this.isInt(val));
							if (values.length > 0) {
								saleIds = values;
							}
							break;
					}
				});
			}

			if (this.isArray(sales, 1) && !this.isArray(saleIds, 1)) {
				// if sales are passed in, use them to limit the displayed sales
				saleIds = sales;
			}

			this.assign(data, {
				addOnPackCursor,
				collectionId,
				hideAddSubtractButtonsForReserved,
				hideUntargetedHolds,
				displayZoomButton: displayZoomButton && this.isFunction(callbacks.zoom),
				groupings,
				layoutType,
				hasPagination,
				hasFiltering,
				moreInfoTooltipPosition,
				preferResaleToQueued,
				preselectedProductId,
				productCardMap: {},
				reconcileTimeoutIds: [],
				saleIds,
				saleTypes,
				sortByOrdinal,
				targetHoldId: null,
				useClientCart,
			});

			this.render({ data, name: renderLayoutName });

			const noResultsElement = this.get(`.no-results`);

			if (noResultsElement != null) {
				this.hide({ target: noResultsElement });
			}
			this.addClass(layoutType);

			promises.push(
				Events.get({
					by: `id`,
					id: eventId,
					force: false,
					then: (event) => (data.event = event),
				})
			);

			if (useClientCart) {
				promises.push(
					ClientCart.get({
						fireImmediately: true,
						subscribe: (cart) => {
							// a cart update was detected
							data.cart = cart;
							data.previousFilters = this.clone(data.filters);
							data.filters = cart.filterConfiguration.filters;

							if (initialized) {
								if (this.isEqual(data.filters, data.previousFilters)) {
									if (layoutType === `LIST`) {
										// List renders in TicketPicker.js. Other layouts render in ProductCard.js where they subscribe to cart updates
										this.reconcile();
									}
								} else {
									this.queueReconcile();
								}
							}
						},
					})
				);
			} else {
				promises.push(
					ShoppingCart.get({
						subscribe: true,
						then: (cart) => {
							// a cart update was detected
							data.cart = cart;

							data.previousFilters = this.clone(data.filters);
							data.filters = cart.filterConfiguration?.[collectionId]?.filters;

							if (initialized) {
								if (this.isEqual(data.filters, data.previousFilters)) {
									// hidden products can be unlocked
									this.reconcile();
								} else {
									// will yield if the filters will call the update code soon
									this.queueReconcile();
								}
							}
						},
					})
				);
			}

			promises.push(
				User.get({
					force: false,
					subscribe: true,
					then: async (user) => {
						const orders = await Orders.getMediaOrders();
						data.orders = orders;

						if (initialized) {
							this.reconcile();
						}

						return data.orders;
					},
					fail: (reason) => {
						data.orders = [];

						if (initialized) {
							this.reconcile();
						}

						return data.orders;
						// user not required, swallow error
					},
				})
			);

			if (hasPagination) {
				promises.push(
					this.create({
						namespace: `Modules/Pagination`,
						setup: {
							name: `pagination`,
							renderFunction: renderProductsFunction,
							renderWrapper: this.get(`.tickets`, this.container),
							countPerPage: LAYOUT_PAGINATION_COUNTS[layoutType],
							callbacks: {
								onPageChange: () => {
									if (this.isFunction(callbacks.onPageChange)) {
										callbacks.onPageChange();
									}
								},
							},
						},
					})
				);
			}

			return this.when({
				promises,
				spread: (event, cart, orders) => {
					// all async data ready
					this.reconcile();
					initialized = true;
				},
			});
		},

		api: {
			reconcileExternal: {
				public: true,
				value(context, startPagingOver = false) {
					this.reconcile(startPagingOver);
				},
			},

			reconcile: {
				value(startPagingOver = false) {
					const { data, instances } = this;
					const { hasFiltering, hasPagination, layoutType, reconcileTimeoutIds } = data;

					// If the ticket picker has pagination, pagination needs to start over if the price code has changed
					if (hasPagination && !startPagingOver) {
						startPagingOver = this.startPagingOverIfPriceCodeChanged();
					}

					// clear any pending reconciles
					this.extract(reconcileTimeoutIds, (timeoutId) => {
						window.clearTimeout(timeoutId);
						return true; // clear the array
					});

					this.getActiveProducts();

					if (hasFiltering) {
						this.getFilteredProducts();
						this.sortFilteredProducts();
					} else {
						data.filteredProducts = data.products;
					}

					if (hasPagination) {
						// calls renderListViewProducts callback
						instances.pagination.updateItems({ items: data.filteredProducts, startPagingOver });
					} else {
						if (this.includes(LIST_VIEW_LAYOUTS, layoutType)) {
							this.renderListViewProducts({ items: data.products });
						} else {
							this.renderSingleMultiViewProducts({ items: data.products });
						}
					}

					this.onResultsCounted();
				},
			},

			// yield for a short period before triggering reconcile(), to avoid firing too often
			queueReconcile: {
				value() {
					const { data } = this;
					const { reconcileTimeoutIds } = data;
					const duration = 300;
					const timeoutId = window.setTimeout(this.reconcile, duration);

					reconcileTimeoutIds.push(timeoutId);
				},
			},

			getActiveProducts: {
				value() {
					const { Utils, data } = this;
					const { cart, collectionId, event, preselectedProductId, saleIds, saleTypes, useClientCart } = data;
					const { groupSaleToken, products } = cart;
					const holds = this.filter(products, { productType: `HOLD` });
					let { sortByOrdinal } = data;
					let activeProducts = [];

					if (this.isInt(collectionId, 1)) {
						const { collections } = event.collectionConfiguration;
						const collection = this.find(collections, { id: collectionId });

						this.each(collection.sales, ({ id }) => {
							const productType = `SALE`;
							const query = { id, productType };
							const sale = this.find(products, query);

							if (this.isObject(sale)) {
								if (this.isArray(saleTypes, 1) && !this.includes(saleTypes, sale.type)) {
									// doesn't match product.type requirement, skip
									return;
								}

								if (this.isArray(saleIds, 1) && !this.includes(saleIds, sale.id)) {
									// doesn't match sale.id requirement from URL param, skip
									return;
								}

								if (!this.isSaleFilteredOut(sale.id)) {
									activeProducts.push(sale);
								}

								this.each(holds, (hold) => {
									const { saleAssignments } = hold;
									const saleId = sale.id;
									const saleQuery = { saleId };
									const assignment = this.find(saleAssignments, saleQuery);

									if (this.isObject(assignment)) {
										// the hold maps back to this sale id

										const existing = this.find(activeProducts, {
											productType: `HOLD`,
											id: hold.id,
										});

										if (!this.isObject(existing)) {
											activeProducts.push(hold);
										}
									}
								});
							} else {
								this.log({
									type: `warning`,
									message: `Collection maps to missing salee ${id}`,
								});
							}
						});

						sortByOrdinal = false;
					} else {
						// no collection ID specified, assume all
						this.each(products, (product) => {
							if (this.isString(groupSaleToken, 1)) {
								// Only show holds
								if (product.productType !== `HOLD` && product.type !== `ADD_ON`) {
									return;
								}
							} else {
								if (product.productType !== `SALE` && product.type !== `ADD_ON`) {
									// Holds are not supported outside the context of collections and group sales
									return;
								}
							}

							if (
								!this.isString(groupSaleToken, 1) &&
								product.productType !== `SALE` &&
								(product.type !== `ADD_ON` || !useClientCart)
							) {
								return;
							}

							if (this.isArray(saleTypes, 1) && !this.includes(saleTypes, product.type)) {
								// doesn't match product.type requirement, skip
								return;
							}

							if (this.isArray(saleIds, 1) && !this.includes(saleIds, product.id)) {
								// doesn't match sale.id requirement from URL param, skip
								return;
							}

							if (this.isInt(data.addOnPackCursor) && !this.includes(product.addOnPackIndexes, data.addOnPackCursor)) {
								// doesn't match add-on pack ordinal
								return;
							}

							if (!this.isSaleFilteredOut(product.id) || (useClientCart && this.includes(saleTypes, `ADD_ON`))) {
								activeProducts.push(product);
							}
						});
					}

					if (sortByOrdinal) {
						activeProducts = Utils.sortBy(activeProducts, `ordinal`);
					}

					if (this.isInt(preselectedProductId)) {
						// if a preselected product is set, move it to the front of the list
						const preselectedProduct = this.find(activeProducts, { id: preselectedProductId });

						if (this.isObject(preselectedProduct)) {
							activeProducts = this.filter(activeProducts, (product) => product.id !== preselectedProductId);
							activeProducts.unshift(preselectedProduct);
						}
					}

					if (activeProducts.length > data.maxLargeTickets) {
						// large number of listings, shrink the size of each ticket
						this.addClass(`small`);
					} else {
						this.removeClass(`small`);
					}

					data.products = activeProducts;
				},
			},

			renderListViewProducts: {
				value({ items: products, startPagingOver = false, wasPaginated = false }) {
					const { App, Config, DOM, Products, ShoppingCart, Templates, data, instances } = this;
					const { DISPLAY_RESALE_STATES } = Config;
					const {
						cart,
						displayZoomButton,
						event,
						groupings,
						hideAddSubtractButtonsForReserved,
						moreInfoTooltipPosition,
						orders,
						preferResaleToQueued,
					} = data;
					const { id: eventId, ticketPickerColorIndicatorSetting } = event;
					const existingElements = this.select(`.ticket[data-product-id]`).value(); // used to find any orphaned elements
					const noResultsElement = this.get(`.no-results`);
					const getIcon = Templates.getHelper(`getIcon`);
					const usedGroupings = [];
					const defaultShowColor = ticketPickerColorIndicatorSetting === `ENABLED`;
					let wrapper = this.get(`.tickets`, this.container);
					let lastTicketElement;

					if (noResultsElement != null) {
						if (products.length === 0) {
							this.show({ target: noResultsElement });
						} else {
							this.hide({ target: noResultsElement });
						}
					}

					const displayTicketsForProducts = async (product) => {
						const {
							color,
							displayState,
							formattedPricePerUnit,
							formattedPriceTotal,
							id,
							isLevels,
							isRemovable: isProductRemovable,
							isReserved,
							optionalSaleRedirectUrl,
							priceLevels,
							productType,
							quantity,
							resaleState,
							selections,
							video,
						} = product;
						const colorDisplayStates = [`OPEN`, `RESALE`]; // sale states that should display color if available
						const idSelector = `[data-product-id="${id}"]`; // Need quotes here
						const typeSelector = `[data-product-type="${productType}"]`;
						const selector = `.ticket${typeSelector}${idSelector}`;
						const hideAddSubtractButtons = isReserved && hideAddSubtractButtonsForReserved && displayState != `QUEUED`;
						const displayPricePerUnit = await Products.isPricePerUnitDisplayed({
							eventId,
							formattedPricePerUnit,
							displayState,
						});
						const soldElsewhere =
							[`SOLD_OUT`, `CLOSED`].includes(product.localState) && this.isString(optionalSaleRedirectUrl, 1);
						let showColor = defaultShowColor;
						let target = this.get(selector);
						let { localState } = product;
						let seatSelections;
						let colorElement;
						let mediaUrl;
						let mediaSelector;
						const isTargetCreated = this.isElement(target);

						if (video) {
							mediaSelector = `.caption`; // all extra label elements

							this.find(orders, (order) => {
								const saleOrder = this.find(order.saleOrders, {
									saleId: id,
									playable: true,
									video: true,
								});

								if (this.isObject(saleOrder)) {
									// playable media found
									mediaUrl = saleOrder.mediaUrl;
								} else {
									// not playable
									mediaSelector += `.AT_LIMIT`; // limit to only hiding the "at limit" label
								}
							});
						}

						const { disabled, externalLink, isRemovable, quantityPickerType } = ShoppingCart.getQuantityPickerOptions({
							allowsPriceLevelExpansion: true,
							hideAddSubtractButtons,
							preferResaleToQueued,
							product,
							mediaUrl,
						});

						const quantityPickerMaximum = this.isInt(product.maxTicketsAllowed)
							? product.maxTicketsAllowed - product.purchasedQuantity
							: product.maxTicketsAllowed;

						// This is also in getFilteredProducts, but going to keep it here for when mode isn't collection
						if (this.Products.isInactive(product)) {
							// ticket is inactive, skip
							return;
						}

						if (displayState === `QUEUED` && resaleState === `AVAILABLE` && preferResaleToQueued) {
							// display details about the resale listings
							const { formattedPriceResale } = product;

							product.summary = formattedPriceResale;
						}

						if (isTargetCreated) {
							const { summary } = product;
							const index = existingElements.indexOf(target);
							const priceElement = this.get(`.price`, target);
							const quantityPicker = instances[`quantity-picker-${id}-${productType}`];
							let content;
							if (displayPricePerUnit && displayState !== `LOCKED` && this.isString(formattedPriceTotal, 1)) {
								content = formattedPriceTotal;
							} else {
								content = summary;
							}

							if (this.isString(content, 1)) {
								priceElement.innerHTML = content;
								this.show({ target: priceElement });
							} else {
								this.hide({ target: priceElement });
							}

							if (index >= 0) {
								// remove the element from the existing lists
								existingElements.splice(index, 1);
							}

							if (this.isArray(groupings)) {
								let i = 0; // find() doesn't support index i argument
								this.find(groupings, (grouping) => {
									const listElement = this.get(`.grouping[index="${i}"] .tickets`);
									const match = this.isMatchForGrouping(product, grouping);

									if (match) {
										wrapper = listElement;
										return true; // break loop
									}

									i++;
								});
							}

							// Tickets should be re-rendered. This happens on filter or sort changes
							if (startPagingOver) {
								wrapper.removeChild(target);
							} else if (quantityPicker != null) {
								quantityPicker.updateRules({
									disabled,
									externalLink,
									isRemovable,
									localState,
									type: quantityPickerType,
									maximum: quantityPickerMaximum,
								});
								quantityPicker.setValue(quantity);
							}
						} else {
							// ticket element not yet created
							const transition = `height`;

							target = document.createElement(`div`);
							this.addClass(`ticket collapsed`, target);
							target.setAttribute(`data-product-id`, id);
							target.setAttribute(`data-product-type`, productType);

							product.currency = cart.currency;
							product.tooltipPosition = `top`;

							if (this.isString(product.thumbUrl, 1)) {
								this.addClass(`has-thumbnail`, target);
							}

							DOM.hide({ target });

							// render the 'ticket' template into the element
							this.render({
								name: `ticket`,
								data: { ...product, displayPricePerUnit, displayZoomButton, moreInfoTooltipPosition },
								target,
							});

							this.defer({
								then: () => DOM.show({ target, transition }),
							});

							if (product.productType === `SALE`) {
								// only track SALEs
								const eventType = `product-view`;
								App.captureInsight({ eventId, eventType, saleId: id });
							}

							const addProductToHandler = (handler) => {
								return (evt = {}) => {
									this.assign(evt, { productId: id, productType });
									return handler(evt);
								};
							};

							this.create({
								namespace: `Inputs/QuantityPicker`,
								setup: {
									name: `quantity-picker-${id}-${productType}`,
									callbacks: {
										increment: addProductToHandler(this.onAdd),
										incrementDisabled: addProductToHandler(this.onAdd),
										decrement: addProductToHandler(this.onSubtract),
										unlock: addProductToHandler(this.onUnlock),
										zoom: addProductToHandler(this.onZoom),
										resale: addProductToHandler(this.onResaleSelection),
										togglePriceLevels: addProductToHandler(this.onTogglePriceLevels),
									},
									rules: {
										disabled,
										externalLink,
										isRemovable,
										localState,
										minimum: 0,
										maximum: quantityPickerMaximum,
										type: quantityPickerType,
									},
								},
								then: (quantityPicker) => {
									quantityPicker.setValue(product.quantity);
								},
							});
						}

						const itemizationElement = this.get(`.itemization`, target);
						const priceLabelElement = this.get(`.priceLabel`, target);
						if (this.isString(product.priceBreakoutMessage, 1) && !soldElsewhere) {
							this.hide({ target: priceLabelElement });

							itemizationElement.innerHTML = product.priceBreakoutMessage;
							this.show({ target: itemizationElement });
						} else {
							this.hide({ target: itemizationElement });

							if (this.isString(product.priceLabel, 1)) {
								// Unsure if price label is still used
								priceLabelElement.innerHTML = product.priceLabel;
								this.show({ target: priceLabelElement });
							} else {
								this.hide({ target: priceLabelElement });
							}
						}

						seatSelections = this.filter(selections, ({ seatLabel, state }) => {
							return state === `ACTIVE` && this.isString(seatLabel);
						});

						if (seatSelections.length > 0) {
							localState = `RESERVED`;
						}

						target.setAttribute(`data-product-state`, localState);
						target.setAttribute(`data-product-display-state`, displayState);

						switch (localState) {
							case `CLOSED`:
							case `SOLD_OUT`:
								if (!this.isString(product.optionalSaleRedirectUrl, 1)) {
									this.addClass(`disabled`, target);
								} else {
									this.addClass(`has-link`, target); // is this being used?
								}
						}

						// calculate any tags that should be displayed on this product
						const tags = [];
						if (resaleState === `AVAILABLE`) {
							if (DISPLAY_RESALE_STATES.includes(displayState)) {
								if (displayState !== `QUEUED` || preferResaleToQueued) {
									tags.push({ classes: [`resale`], icon: `resale`, text: `Resale`, action: `viewResaleTag` });
								}
							}
						}

						if (displayState === `QUEUED`) {
							tags.push({ icon: `time`, text: `Waitlist`, action: `viewWaitlistTag` });
						} else if (displayState === `SOLD_OUT`) {
							if (!this.isString(optionalSaleRedirectUrl)) {
								tags.push({ text: `Sold Out` });
							}
						} else if (displayState === `CLOSED`) {
							tags.push({ text: `Sale Over` });
						} else if (displayState === `LOCKED`) {
							tags.push({ icon: `lock-3`, text: `Code Needed` });
						} else if (displayState === `AVAIL_SOON`) {
							tags.push({ text: `Available Soon` });
						} else if (displayState === `AVAIL_AT_DOOR`) {
							tags.push({ text: `Sold at Door` });
						} else if (displayState === `OPEN`) {
							const { countdownMessage } = product;

							if (this.isString(countdownMessage, 1)) {
								tags.push({ text: countdownMessage });
							}
						}

						this.defer({
							then: async () => {
								const tagsElement = this.get(`.tags`, target);
								const allTagTexts = this.map(tags, `text`);
								const existingTagElements = this.select(`.tag`, tagsElement);

								// check for tags that are no longer valid
								this.each(existingTagElements, (tagElement) => {
									const { dataset } = tagElement;
									const { text } = dataset;

									if (!allTagTexts.includes(text)) {
										// not included in the current list of tags, remove it
										this.hide({ target: tagElement, transition: `height`, remove: true });
									}
								});

								if (tags.length > 0) {
									// ensure the list is showing
									await this.show({ target: tagsElement });

									this.each(tags, async (tag) => {
										const { action, classes, icon, text } = tag;
										const existingTag = this.get(`[data-text="${text}"]`, tagsElement);

										if (!this.isElement(existingTag)) {
											// the tag doesn't already exist, create it
											let newTagElement;
											if (this.isString(action, 1)) {
												newTagElement = document.createElement(`a`);
											} else {
												newTagElement = document.createElement(`div`);
											}
											const iconString = icon === undefined ? `` : getIcon(icon) + `&nbsp;`;
											const classString = classes === undefined ? `` : ` ` + classes.join(` `);

											newTagElement.setAttribute(`data-text`, text);
											this.addClass(`tag badge card-badge small ${classString}`, newTagElement);

											if (this.isString(action, 1)) {
												this.addClass(`has-action`, newTagElement);
												newTagElement.setAttribute(`href`, `#`);
												newTagElement.setAttribute(`action`, action);
											}

											newTagElement.innerHTML = `${iconString}${text}`;

											await this.hide({ target: newTagElement });
											tagsElement.appendChild(newTagElement);
											this.show({ target: newTagElement, transition: `height` });
										}
									});
								} else {
									// no tags to show, hide the wrapper
									await this.hide({ target: tagsElement, transition: `height` });
									tagsElement.innerHTML = ``; // reset the content
								}
							},
						});

						// TODO: figure out what logic was removed
						// this.toggleButtonAtLimit({ atLimit: localState === `AT_LIMIT`, buttons: addButton });
						// this.toggleButtonAtLimit({ atLimit: localState === `AT_LIMIT`, buttons: resaleButton });

						if (!this.includes(colorDisplayStates, displayState)) {
							showColor = false;
						}

						colorElement = this.get(`.color-highlight`, target);
						if (showColor && this.isString(color, 1)) {
							colorElement.style.backgroundColor = color;
							this.show({ target: colorElement });
							this.addClass(`has-color`, target);
						} else {
							this.hide({ target: colorElement });
							this.removeClass(`has-color`, target);
						}

						if (isLevels) {
							// check minBehavior per-priceLevel
							this.each(priceLevels, (priceLevel) => {
								const {
									isAddable: isLevelAddable,
									isRemovable: isLevelRemovable,
									value: priceLevelOptionId,
								} = priceLevel;

								const addProductLevelsToHandler = (handler) => {
									return (evt = {}) => {
										this.assign(evt, { productId: id, productType, priceLevelOptionId });
										return handler(evt);
									};
								};

								const levelSelections = this.filter(product.selections, { priceLevelOptionId });
								const levelElement = this.get(`[data-price-level-option-id="${priceLevelOptionId}"]`, target);
								const rules = {
									disabled,
									isAddable: isLevelAddable && localState !== `AT_LIMIT`,
									isRemovable: isLevelRemovable,
									type: hideAddSubtractButtons ? `HIDDEN` : `OPEN`,
								};
								const quantity = levelSelections.length;

								if (isTargetCreated) {
									const quantityPicker = instances[`quantity-picker-${id}-${productType}-${priceLevelOptionId}`];

									if (quantityPicker != null) {
										quantityPicker.updateRules(rules);
										quantityPicker.setValue(quantity);
									}
								} else {
									this.create({
										namespace: `Inputs/QuantityPicker`,
										setup: {
											name: `quantity-picker-${id}-${productType}-${priceLevelOptionId}`,
											callbacks: {
												increment: addProductLevelsToHandler(this.onAdd),
												incrementDisabled: addProductLevelsToHandler(this.onAdd),
												decrement: addProductLevelsToHandler(this.onSubtract),
												zoom: addProductLevelsToHandler(this.onZoom),
											},
											rules,
										},
										then: (quantityPicker) => {
											quantityPicker.setValue(quantity);
										},
									});
								}

								const itemizationElement = this.get(`.price-level-itemization`, levelElement);
								if (this.isString(priceLevel.priceBreakoutMessage, 1)) {
									itemizationElement.innerHTML = priceLevel.priceBreakoutMessage;
								} else {
									this.hide({ target: itemizationElement });
								}
							});
						}

						if (isProductRemovable) {
							this.removeClass(`hide-remove`, target);
						} else {
							this.addClass(`hide-remove`, target);
						}

						this.removeClass(`has-media`, target);

						if (video) {
							const container = target;

							if (this.isString(mediaUrl, 1)) {
								// playable media found
								const link = this.get(`.media-link`, target);
								link.setAttribute(`href`, mediaUrl);
								this.addClass(`has-media`, target);
							} else {
								this.hide({ selector: mediaSelector, container });
							}
						}

						if (this.isArray(groupings)) {
							let index = 0; // find() doesn't pass index i argument
							this.find(groupings, (grouping) => {
								const listElement = this.get(`.grouping[index="${index}"] .tickets`);
								const match = this.isMatchForGrouping(product, grouping);

								if (match) {
									wrapper = listElement;

									if (!this.includes(usedGroupings, grouping)) {
										usedGroupings.push(grouping);
									}
									return true; // break loop
								}

								index++;
							});
						} else {
							wrapper = this.get(`.tickets`, this.container);
						}

						if (target.parentNode !== wrapper) {
							if (lastTicketElement) {
								lastTicketElement.insertAdjacentElement(`afterend`, target);
							} else {
								wrapper.prepend(target);
							}
						}

						lastTicketElement = target; // save the last ticket element for insertion of the next ticket
					};

					const promises = products.map(async (product) => {
						return await displayTicketsForProducts(product);
					});

					this.when({
						promises,
						then: () => {
							this.toggleGroupingsVisibility(usedGroupings);

							while (existingElements.length > 0) {
								const target = existingElements.pop();
								const [remove, transition] = [true, wasPaginated ? `none` : `height`];
								DOM.hide({ remove, target, transition });
							}
						},
					});

					return promises;
				},
			},

			// returns an array of promises to notify Pagination.js when to display
			renderSingleMultiViewProducts: {
				value({ items: products }) {
					const { data, App, DOM } = this;
					const {
						cart,
						displayZoomButton,
						event,
						groupings,
						hideAddSubtractButtonsForReserved,
						layoutType,
						productCardMap,
						useClientCart,
					} = data;
					const existingElements = this.select(`.ticket[data-product-id]`).value(); // used to find any orphaned elements
					const orientation = ORIENTATION[data.layoutType];
					const noResultsElement = this.get(`.no-results`);
					const isSingleColumnVariation = layoutType === `SINGLE_COLUMN` || layoutType === `ARENA_SINGLE_COLUMN`;
					const usedGroupings = [];
					let wrapper = this.get(`.tickets`, this.container);
					let promises = [];

					if (products.length === 0) {
						this.show({ target: noResultsElement });
					} else {
						this.hide({ target: noResultsElement });
					}

					this.each(products, (product) => {
						const { id, grouping, isReserved } = product;
						let ticketContainer = this.get(`.ticket[data-product-id=${id}]`);

						const properties = {
							item: { id },
							eventId: event.id,
							orientation,
							expandImageOnHover: false,
							imageMode: isSingleColumnVariation ? `advanced` : `simple`,
							hideAddSubtractButtons: isReserved && hideAddSubtractButtonsForReserved,
							dynamicallySizeOnMobile: isSingleColumnVariation,
							dynamicallySizeOnDesktop: isSingleColumnVariation,
							useClientCart: useClientCart,
							callbacks: {
								zoom: this.isFunction(this.callbacks.zoom) && displayZoomButton ? this.callbacks.zoom : null,
							},
						};

						if (this.isArray(groupings)) {
							wrapper = this.get(`.grouping[index="${grouping.index}"] .tickets`);

							if (!this.includes(usedGroupings, grouping)) {
								usedGroupings.push(grouping);
							}
						}

						if (this.isElement(ticketContainer)) {
							const index = existingElements.indexOf(ticketContainer);

							// Need to remove and reappend element to avoid flashing of screen
							// Use ticketContainer.parentElement for group switching
							ticketContainer.parentElement.removeChild(ticketContainer);

							if (index >= 0) {
								// remove the element from the existing lists
								existingElements.splice(index, 1);
							}

							wrapper.appendChild(ticketContainer);
						} else {
							// ticket element not yet created
							const eventType = `product-view`; // for analytics
							const cardContainer = document.createElement(`div`);

							// create a new container for the ticket
							ticketContainer = document.createElement(`div`);
							this.addClass(`ticket`, ticketContainer);
							ticketContainer.setAttribute(`data-product-id`, id);
							ticketContainer.appendChild(cardContainer); // attach the container for the card
							wrapper.appendChild(ticketContainer); // add ticket to the list

							product.currency = cart.currency;

							properties.container = cardContainer;

							promises.push(
								this.create({
									namespace: `Modules/ProductCard`,
									setup: properties,
									then: (card) => {
										productCardMap[product.id] = card;
									},
								})
							);

							if (product.productType === `SALE`) {
								// only track SALEs
								App.captureInsight({ eventId: event.id, eventType, saleId: id });
							}
						}
					});

					this.toggleGroupingsVisibility(usedGroupings);

					while (existingElements.length > 0) {
						const target = existingElements.pop();
						const id = target.getAttribute(`data-product-id`);
						const exisingProductCard = productCardMap[id];
						const [done, remove, transition] = [true, true, `height`];

						if (exisingProductCard != null) {
							exisingProductCard.destroy({ remove, transition });
							delete productCardMap[id];
						}

						DOM.hide({ done, remove, target, transition });
					}

					return promises;
				},
			},

			collapseLevels: {
				public: true,
				value() {
					this.select(`.ticket`).removeClass(`expanded`).addClass(`collapsed`); // reset each ticket's state
					this.removeClass(`expanded`);
				},
			},

			getActiveProductCount: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { products } = data;

					return products.length;
				},
			},

			updateFilter: {
				public: true,
				value(context, saleIdsRemoved) {
					this.data.saleIdsRemoved = saleIdsRemoved;
					this.reconcile();
				},
			},

			isSaleFilteredOut: {
				value(id) {
					const { saleIdsRemoved } = this.data;

					if (this.isObject(saleIdsRemoved) && saleIdsRemoved[id] === true) {
						return true;
					}
					return false;
				},
			},

			// Possibly refactor to have the FilterSearchBar run the filtering logic
			getFilteredProducts: {
				value() {
					const { data } = this;
					const { filterSortValues, hideUntargetedHolds, targetHoldId, products } = data;
					const filteredProducts = [];

					if (this.isInt(targetHoldId)) {
						const targetHold = this.find(products, { id: targetHoldId, productType: `HOLD` });
						filteredProducts.push(targetHold);
					} else {
						this.each(products, (product) => {
							const { productType, quantity } = product;
							let filterOut;
							let metadataCollection = product.metadataCollection;

							if (this.isInt(data.addOnPackCursor)) {
								metadataCollection = product.addOnPackMetadata[data.addOnPackCursor];
							}

							const isHiddenUntargetedHold = hideUntargetedHolds && productType === `HOLD` && quantity === 0;
							const isInactive = this.Products.isInactive(product);

							if (isHiddenUntargetedHold || isInactive) {
								// should be skipped
								filterOut = true;
							} else {
								// should be shown
								filterOut = false;
							}

							if (filterSortValues != null) {
								this.each(filterSortValues.filter, (currentFilter, key) => {
									if (!filterOut && currentFilter != null) {
										if (metadataCollection != null) {
											const matchingProductMetadataCollectionArray = this.filter(
												metadataCollection,
												(metadataCollection) => {
													return metadataCollection.metadataId.toString() === key;
												}
											);

											if (matchingProductMetadataCollectionArray.length > 0) {
												filterOut = true;

												this.each(matchingProductMetadataCollectionArray, (matchingProductMetadataCollection) => {
													const metadataOptionValue = matchingProductMetadataCollection.metadataOptionValue;
													if (this.isString(currentFilter)) {
														// SINGLE
														if (currentFilter === matchingProductMetadataCollection.metadataOptionId.toString()) {
															filterOut = false;
														}
													} else if (this.isArray(currentFilter, 1)) {
														// MULTI and MULTI_CHECKBOX
														if (
															this.includes(
																currentFilter,
																matchingProductMetadataCollection.metadataOptionId.toString()
															)
														) {
															filterOut = false;
														}
													} else if (this.isObject(currentFilter)) {
														// RANGE
														const { min, max } = currentFilter;
														if (min != null && max != null) {
															if (Math.round(metadataOptionValue) >= min && Math.round(metadataOptionValue) <= max) {
																filterOut = false;
															}
														}
													}
												});
											} else {
												filterOut = true;
											}
										} else {
											filterOut = true;
										}
									}
								});
							}

							if (!filterOut) {
								filteredProducts.push(product);
							}
						});
					}

					data.filteredProducts = filteredProducts;
				},
			},

			sortFilteredProducts: {
				value() {
					const { data, Config } = this;
					const { filteredProducts, filterSortValues, groupings } = data;
					const { COLLECTION_SORT_OPTIONS } = Config;
					const sortValue =
						filterSortValues != null ? filterSortValues.sort : COLLECTION_SORT_OPTIONS.RECOMMENDED.value;
					const usedGroupings = [];
					let sortedProducts = this.clone(filteredProducts);

					if (this.isArray(groupings, 2)) {
						this.each(sortedProducts, (product) => {
							let index = 0; // find() doesn't pass index i argument
							product.grouping = this.find(groupings, (grouping) => {
								const match = this.isMatchForGrouping(product, grouping);

								if (match) {
									if (!this.includes(usedGroupings, grouping)) {
										grouping.index = index;
										usedGroupings.push(grouping);
									}

									return true;
								}

								index++;
							});
						});

						if (usedGroupings.length > 1) {
							sortedProducts = sortedProducts.sort((a, b) => {
								const aGrouping = a.grouping;
								const bGrouping = b.grouping;

								if (aGrouping != null && bGrouping != null) {
									return aGrouping.index - bGrouping.index;
								} else if (aGrouping != null) {
									return -1;
								} else if (bGrouping != null) {
									return 1;
								} else {
									switch (sortValue) {
										case COLLECTION_SORT_OPTIONS.PRICE_LOWEST_TO_HIGHEST.value:
											return a.advertisedPrice - b.advertisedPrice;
										case COLLECTION_SORT_OPTIONS.PRICE_HIGHEST_TO_LOWEST.value:
											return b.advertisedPrice - a.advertisedPrice;
										default:
											return 0;
									}
								}
							});
						}
					}

					if (usedGroupings.length <= 1) {
						switch (sortValue) {
							case COLLECTION_SORT_OPTIONS.PRICE_LOWEST_TO_HIGHEST.value:
								sortedProducts = sortedProducts.sort((a, b) => a.advertisedPrice - b.advertisedPrice);
								break;
							case COLLECTION_SORT_OPTIONS.PRICE_HIGHEST_TO_LOWEST.value:
								sortedProducts = sortedProducts.sort((a, b) => b.advertisedPrice - a.advertisedPrice);
								break;
							default: // COLLECTION_SORT_OPTIONS.RECOMMENDED.value
								break;
						}
					}

					this.assign(data, {
						usedGroupings,
						filteredProducts: sortedProducts,
					});
				},
			},

			toggleGroupingsVisibility: {
				value(usedGroupings) {
					const { DOM, data } = this;
					const { groupings } = data;

					if (this.isArray(groupings)) {
						const { hide, show } = DOM;
						const showHeader = usedGroupings.length > 1;
						const transition = `height`;

						this.each(groupings, (grouping, i) => {
							const showElement = this.includes(usedGroupings, grouping);
							const wrapper = this.get(`.grouping[index="${i}"]`);
							let target = wrapper;
							let action;

							// the element as a whole, hidden when empty
							action = showElement ? show : hide;
							action({ target, transition });

							// the header, hidden when only one element is visible
							action = showHeader ? show : hide;
							target = this.get(`.tickets-header`, wrapper);
							if (!this.isElement(target)) {
								target = this.get(`hr`, wrapper);
							}

							action({ target, transition });
						});
					}
				},
			},

			isMatchForGrouping: {
				value(product, grouping) {
					const { behavior } = grouping;

					if (behavior === `UNLOCKED`) {
						// targeting unlocked products
						if (product.isUnlocked) {
							return true;
						}
					} else if (behavior === `ALL`) {
						return true;
					}

					return false;
				},
			},

			updateFilterSortValues: {
				public: true,
				value(context, values) {
					this.data.filterSortValues = values;
					this.reconcile(true);
				},
			},

			onResultsCounted: {
				value() {
					if (this.isFunction(this.callbacks.onResultsCounted)) {
						this.callbacks.onResultsCounted(this.data.filteredProducts.length);
					}
				},
			},

			setTargetHold: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { id: targetHoldId } = options;

					this.assign(data, { targetHoldId });
					this.reconcile();
				},
			},

			setAddOnPackCursor: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { value } = options;

					data.addOnPackCursor = value;
				},
			},

			onAdd: {
				value({ productId, productType, priceLevelOptionId }) {
					const { App, Config, ClientCart, ShoppingCart, data } = this;
					const { ACTIVE_STATES } = Config;
					const { cart, useClientCart } = data;
					const { products } = cart;
					const product = this.find(products, { id: productId, productType }); // find the clicked sale
					const { localState } = product;
					const target = this.get(`.ticket[data-product-id="${productId}"][data-product-type="${productType}"]`);
					const levelWrapper = this.get(`[data-price-level-option-id="${priceLevelOptionId}"]`, target);

					if (localState === `AT_Q_LIMIT`) {
						const type = `warning`;
						const message = `A waitlist ticket cannot be combined with any other type of ticket.`;
						return App.notifyUser({ type, message });
					} else if (this.includes(ACTIVE_STATES, localState)) {
						const params = {};
						let priceLevelOptionId;

						if (productType === `SALE`) {
							params.saleId = product.id;
							params.tierId = product.tiers[0].id;
						}
						// NOTE: Holds have selection.saleId set once an underlying sale is specified (e.g. seat selection)

						if (this.isElement(levelWrapper)) {
							const { dataset: levelDataset } = levelWrapper;
							const { priceLevelOptionId: priceLevelOptionIdString } = levelDataset;

							priceLevelOptionId = this.parseInt(priceLevelOptionIdString);
							this.assign(params, { priceLevelOptionId });
						} else {
							this.collapseLevels(); // add on a ticket with no price levels, collapse any opened price levels
						}

						if (useClientCart) {
							ClientCart.incrementProduct({ id: productId, priceLevelOptionId });
						} else {
							ShoppingCart.incrementProduct({ id: productId, priceLevelOptionId, productType });
						}
					}
				},
			},

			onSubtract: {
				value({ productId, productType, priceLevelOptionId }) {
					const { ClientCart, ShoppingCart, data } = this;
					const { useClientCart } = data;
					const target = this.get(`.ticket[data-product-id="${productId}"][data-product-type="${productType}"]`);
					const levelWrapper = this.get(`[data-price-level-option-id="${priceLevelOptionId}"]`, target);

					if (!this.isElement(levelWrapper)) {
						this.collapseLevels(); // remove on a ticket with no price levels, collapse any opened price levels
					}

					if (useClientCart) {
						ClientCart.decrementProduct({ id: productId, priceLevelOptionId });
					} else {
						ShoppingCart.decrementProduct({ id: productId, priceLevelOptionId, productType });
					}
				},
			},

			onUnlock: {
				value({ productId, productType }) {
					const { App, data } = this;
					const { cart } = data;
					const { eventId, products } = cart;
					const product = this.find(products, { id: productId, productType });
					const type = `EnterAccessCode`;
					const { icon } = product;
					const title = product.name;
					const scope = `event`;
					const setup = { eventId, icon, scope, title };
					return App.loadOverlay({ type, setup });
				},
			},

			onZoom: {
				value({ productId, productType }) {
					const { callbacks, data } = this;
					const { cart } = data;
					const { products } = cart;
					const product = this.find(products, { id: productId, productType });
					const { selections } = product;
					const seatLabels = this.map(selections, `seatLabel`);

					callbacks.zoom({ seatLabels });
				},
			},

			onResaleSelection: {
				value({ productId, productType }) {
					const { App, data } = this;
					const { event } = data;
					const type = `EventListings`;
					const { id: eventId } = event;
					const setup = { eventId, productId, productType };

					App.loadOverlay({ type, setup });
				},
			},

			onTogglePriceLevels: {
				value({ productId, productType }) {
					const ticketWrapper = this.get(`.ticket[data-product-id="${productId}"][data-product-type="${productType}"]`);
					const isCollapsed = this.hasClass(`collapsed`, ticketWrapper); // store the current state

					this.collapseLevels(); // ensure all price levels are collapsed - need this during merge

					if (isCollapsed) {
						this.removeClass(`collapsed`, ticketWrapper);
						this.addClass(`expanded`, ticketWrapper);
						this.addClass(`expanded`);
					}
				},
			},

			/**
			 * Determines if the user has access code locked products displayed based on the filtered products
			 * @param {Object} context
			 */
			getHasAccessCodeLock: {
				public: true,
				value(context) {
					const { data } = this;
					const { filteredProducts } = data;
					let hasAccessCodeLock = false;

					this.each(filteredProducts, (product) => {
						if (product.displayState === `LOCKED`) {
							hasAccessCodeLock = true;
							return true;
						}
					});

					return hasAccessCodeLock;
				},
			},

			/**
			 * Checks if the price code has changed since the last check and starts paging over if it has.
			 * @returns {boolean} - Returns true if the price code has changed, otherwise false.
			 */
			startPagingOverIfPriceCodeChanged: {
				value() {
					const isUseAgentPriceCode = this.Events.isUseAgentPriceCode();

					if (!isUseAgentPriceCode) {
						return false; // if not using agent price code, no need to check
					}

					const { data } = this;
					const { previousPriceCode } = data;
					const currentPriceCode = this.AgentPriceCode.get();
					let hasChanged = false;

					if (previousPriceCode != null) {
						hasChanged = !this.isEqual(previousPriceCode, currentPriceCode);
					}

					// Update the previous price code to the current one
					data.previousPriceCode = currentPriceCode;

					return hasChanged;
				},
			},
		},

		destructor() {
			this.data.productCardMap = null;
		},

		listeners: [
			{
				event: `click`,
				filter: `[action]`,
				handler(evt) {
					const { currentTarget, target } = evt;
					const action = currentTarget.getAttribute(`action`);

					if (this.hasClass(`disabled`, currentTarget)) {
						evt.preventDefault();
						return;
					}

					const { App, DOM, data } = this;
					const { event, useClientCart } = data;
					const ticketWrapper = DOM.closest(target, `.ticket`);
					const { dataset: ticketDataset } = ticketWrapper;
					const { productId: productIdString, productType } = ticketDataset;
					const productId = this.parseInt(productIdString);

					switch (action) {
						case `more-info`: {
							// detect clicks inside the TicketPicker
							const { products } = data;
							const { id: eventId } = event;
							const product = this.find(products, { id: productId, productType });

							const { displayState } = product;
							let setup;
							let type;

							if (displayState === `LOCKED`) {
								const { icon, name } = product;
								setup = { eventId, icon, scope: `event`, title: name };
								type = `EnterAccessCode`;
							} else if (product.layout === `MEDIA`) {
								const theme = `DARK`; // ensure DARK theme is always used
								setup = { eventId, productId, productType, theme };
								type = `VideoDetails`;
							} else {
								setup = { eventId, productId, productType, useClientCart };
								type = `TicketInfo`;
							}

							if (productType === `SALE`) {
								const eventType = `product-click`;
								const saleId = productId;

								App.captureInsight({ eventType, saleId });
							}

							App.loadOverlay({ type, setup });
							break;
						}
						case `viewResaleTag`: {
							evt.stopPropagation();
							App.loadOverlay({
								type: `InformationModal`,
								setup: {
									informationType: `resaleBadge`,
								},
								replace: false,
							});
							break;
						}
						case `viewWaitlistTag`: {
							evt.stopPropagation();
							App.loadOverlay({
								type: `InformationModal`,
								setup: {
									informationType: `waitlistBadge`,
								},
								replace: false,
							});
							break;
						}
					}
				},
			},
		],
	});
});

define("Modules/TicketPicker.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Modules/TicketPicker/listView"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"groupings") : depth0),{"name":"each","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":18},"end":{"line":1,"column":206}}})) != null ? stack1 : "")
    + " ";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"grouping\" index=\""
    + container.escapeExpression(container.lambda((data && lookupProperty(data,"index")), depth0))
    + "\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"label") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":1,"column":80},"end":{"line":1,"column":161}}})) != null ? stack1 : "")
    + " <div class=\"tickets\"></div> </div> ";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"tickets-header\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"label") : depth0), depth0))
    + "</div> ";
},"5":function(container,depth0,helpers,partials,data) {
    return " <hr /> ";
},"7":function(container,depth0,helpers,partials,data) {
    return " <div class=\"tickets\"></div> ";
},"9":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"no-results\"> "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"magnifying-glass",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":299},"end":{"line":1,"column":329}}}))
    + " <h4>No items found</h4> <p>Try changing the filters or search term</p> </div> <div name=\"pagination\"></div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"groupings") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":251}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"hasPagination") : depth0),{"name":"if","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":252},"end":{"line":1,"column":445}}})) != null ? stack1 : "");
},"useData":true});

this["Templates"]["Modules/TicketPicker/main"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<div class=\"main\"></div>";
},"useData":true});

this["Templates"]["Modules/TicketPicker/singleMultiView"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"groupings") : depth0),{"name":"each","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":18},"end":{"line":1,"column":206}}})) != null ? stack1 : "")
    + " ";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"grouping\" index=\""
    + container.escapeExpression(container.lambda((data && lookupProperty(data,"index")), depth0))
    + "\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"label") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":1,"column":80},"end":{"line":1,"column":161}}})) != null ? stack1 : "")
    + " <div class=\"tickets\"></div> </div> ";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"tickets-header\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"label") : depth0), depth0))
    + "</div> ";
},"5":function(container,depth0,helpers,partials,data) {
    return " <hr /> ";
},"7":function(container,depth0,helpers,partials,data) {
    return " <div class=\"tickets\"></div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"groupings") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":251}}})) != null ? stack1 : "")
    + " <div class=\"no-results\"> "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(alias1,"magnifying-glass",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":277},"end":{"line":1,"column":307}}}))
    + " <h4>No items found</h4> <p>Try changing the filters or search term</p> </div> <div name=\"pagination\"></div>";
},"useData":true});

this["Templates"]["Modules/TicketPicker/ticket"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"thumbnail\">  <img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"thumbUrl") : depth0), depth0))
    + "\" /> <div class=\"background\" style=\"background-image:url('"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"thumbUrl") : depth0), depth0))
    + "')\"></div> </div> ";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"summary") : depth0), depth0)) != null ? stack1 : "")
    + " ";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"formattedPriceTotal") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":945},"end":{"line":1,"column":1029}}})) != null ? stack1 : "")
    + " ";
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"formattedPriceTotal") : depth0), depth0)) != null ? stack1 : "")
    + " ";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"footnote AT_LIMIT AT_Q_LIMIT OPEN QUEUED RESERVED LOCKED\">  "
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"formattedPricePerUnit") : depth0), depth0)) != null ? stack1 : "")
    + " </div> ";
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"priceLevels") : depth0),{"name":"each","hash":{},"fn":container.program(11, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2094},"end":{"line":1,"column":2736}}})) != null ? stack1 : "")
    + " ";
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"level\" data-price-level-option-id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"value") : depth0), depth0))
    + "\"> <div class=\"wrapper\"> <div class=\"info\"> <div class=\"name\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"label") : depth0), depth0))
    + "</div> <div class=\"price OPEN QUEUED AT_LIMIT AT_Q_LIMIT RESERVED\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"formattedPrice") : depth0), depth0)) != null ? stack1 : "")
    + "</div> <div class=\"price-level-itemization\"></div> </div> </div> <div name=\"quantity-picker-"
    + alias2(alias1(((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"id")), depth0))
    + "-"
    + alias2(alias1(((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"productType")), depth0))
    + "-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"value") : depth0), depth0))
    + "\" class=\"quantity-picker\"></div>  <a class=\"media-link\" href=\"#\"> "
    + alias2(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"playsymbol","circle-border",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":2674},"end":{"line":1,"column":2714}}}))
    + " </a> </div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"ticket-overlay\"></div> <div class=\"color-highlight\"></div> <div class=\"item\"> <div class=\"wrapper\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"thumbUrl") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":196},"end":{"line":1,"column":460}}})) != null ? stack1 : "")
    + "   <div class=\"info\"> <div class=\"tags\"></div> <div class=\"name\">"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</div> <div class=\"price AT_LIMIT AT_Q_LIMIT AVAIL_AT_DOOR AVAIL_SOON CLOSED FREE LOCKED OPEN QUEUED RESALE RESERVED SOLD_OUT\">  "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"displayState") : depth0),"LOCKED",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":1,"column":887},"end":{"line":1,"column":1042}}})) != null ? stack1 : "")
    + " </div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"displayPricePerUnit") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1050},"end":{"line":1,"column":1260}}})) != null ? stack1 : "")
    + "  <div class=\"priceLabel\">"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"priceLabel") : depth0), depth0))
    + "</div> <div class=\"itemization\"></div> </div> <a href=\"#\" class=\"more-info-i-button hide-desktop\" action=\"more-info\" aria-label=\"more info\">"
    + alias3(lookupProperty(helpers,"getIcon").call(alias1,"moreinfo-unfilled",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1513},"end":{"line":1,"column":1544}}}))
    + "</a> <a href=\"#\" class=\"more-info-i-button hide-mobile\" action=\"more-info\" aria-label=\"more info\" data-tooltip-text=\"More info\" data-tooltip-position="
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"moreInfoTooltipPosition") : depth0), depth0))
    + ">"
    + alias3(lookupProperty(helpers,"getIcon").call(alias1,"moreinfo-unfilled",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1722},"end":{"line":1,"column":1753}}}))
    + "</a> <a href=\"#\" class=\"more-info-hover\" action=\"more-info\" aria-label=\"more info\"></a> <div name=\"quantity-picker-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"productType") : depth0), depth0))
    + "\" class=\"quantity-picker\"></div>  <a class=\"media-link\" href=\"#\"> "
    + alias3(lookupProperty(helpers,"getIcon").call(alias1,"playsymbol","circle-border",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":2014},"end":{"line":1,"column":2054}}}))
    + " </a> </div> </div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"priceLevels") : depth0),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":2074},"end":{"line":1,"column":2744}}})) != null ? stack1 : "");
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=TicketPicker.js.map